import { useRef } from 'react'

import useSWR, { mutate as globalMutate } from 'swr'

import type { NgmClusterContext } from '~/api/ngm'

import {
  GetTopruSummaryPlanParams,
  GetTopruSummaryParams,
  GetTopruUserStatsParams,
  TopruSummaryEnvelope,
  TopruSummaryPlanEnvelope,
  TopruUserStatsEnvelope,
  getTopruSummary,
  getTopruSummaryPlan,
  getTopruUserStats
} from './api'

interface QueryOptions {
  enabled?: boolean
  refetchOnWindowFocus?: boolean
  keepPreviousData?: boolean
}

// react-query style result so the page can use familiar field names.
interface QueryResult<T> {
  data: T | undefined
  isLoading: boolean
  isFetching: boolean
  isFetched: boolean
  refetch: () => Promise<T | undefined>
}

// SWR v1 doesn't expose keepPreviousData; emulate it by holding the last
// non-undefined value across key changes.
function useKeepPreviousData<T>(
  value: T | undefined,
  enabled: boolean
): T | undefined {
  const ref = useRef<T | undefined>(undefined)
  if (!enabled) {
    ref.current = value
    return value
  }
  if (value !== undefined) {
    ref.current = value
  }
  return value !== undefined ? value : ref.current
}

export function useTopruSummaryQuery(
  ctx: NgmClusterContext | null,
  params: GetTopruSummaryParams,
  options: QueryOptions = {}
): QueryResult<TopruSummaryEnvelope> {
  const enabled = options.enabled !== false && !!ctx?.clusterId
  const key = enabled
    ? (['topru-summary', ctx!.clusterId, ctx!.orgExternalId, params] as const)
    : null
  const swr = useSWR<TopruSummaryEnvelope | undefined>(
    key,
    () => getTopruSummary(ctx!, params),
    {
      revalidateOnFocus: options.refetchOnWindowFocus ?? false
    }
  )
  const displayedData = useKeepPreviousData(
    swr.data,
    !!options.keepPreviousData
  )
  const fetched = swr.data !== undefined || swr.error !== undefined
  return {
    data: displayedData,
    isLoading: !fetched && swr.isValidating,
    isFetching: swr.isValidating,
    isFetched: fetched,
    refetch: () => swr.mutate() as Promise<TopruSummaryEnvelope | undefined>
  }
}

export function useTopruSummaryPlanQuery(
  ctx: NgmClusterContext | null,
  params: GetTopruSummaryPlanParams,
  options: QueryOptions = {}
): QueryResult<TopruSummaryPlanEnvelope> {
  const enabled = options.enabled !== false && !!ctx?.clusterId
  const key = enabled
    ? ([
        'topru-summary-plan',
        ctx!.clusterId,
        ctx!.orgExternalId,
        params
      ] as const)
    : null
  const swr = useSWR<TopruSummaryPlanEnvelope | undefined>(
    key,
    () => getTopruSummaryPlan(ctx!, params),
    {
      revalidateOnFocus: options.refetchOnWindowFocus ?? false
    }
  )
  const displayedData = useKeepPreviousData(
    swr.data,
    !!options.keepPreviousData
  )
  const fetched = swr.data !== undefined || swr.error !== undefined
  return {
    data: displayedData,
    isLoading: !fetched && swr.isValidating,
    isFetching: swr.isValidating,
    isFetched: fetched,
    refetch: () => swr.mutate() as Promise<TopruSummaryPlanEnvelope | undefined>
  }
}

export function useTopruUserStatsQuery(
  ctx: NgmClusterContext | null,
  params: GetTopruUserStatsParams,
  options: QueryOptions = {}
): QueryResult<TopruUserStatsEnvelope> {
  const enabled = options.enabled !== false && !!ctx?.clusterId
  const key = enabled
    ? ([
        'topru-user-stats',
        ctx!.clusterId,
        ctx!.orgExternalId,
        params
      ] as const)
    : null
  const swr = useSWR<TopruUserStatsEnvelope | undefined>(
    key,
    () => getTopruUserStats(ctx!, params),
    {
      revalidateOnFocus: options.refetchOnWindowFocus ?? false
    }
  )
  const displayedData = useKeepPreviousData(
    swr.data,
    !!options.keepPreviousData
  )
  const fetched = swr.data !== undefined || swr.error !== undefined
  return {
    data: displayedData,
    isLoading: !fetched && swr.isValidating,
    isFetching: swr.isValidating,
    isFetched: fetched,
    refetch: () => swr.mutate() as Promise<TopruUserStatsEnvelope | undefined>
  }
}

// Convenience helper to invalidate all topru data for a cluster.
export function invalidateTopruCache(clusterId: string) {
  return globalMutate(
    (key: unknown) =>
      Array.isArray(key) &&
      (key[0] === 'topru-summary' ||
        key[0] === 'topru-summary-plan' ||
        key[0] === 'topru-user-stats') &&
      key[1] === clusterId,
    undefined,
    { revalidate: true }
  )
}
